// FRACAS Context - Failure Reporting, Analysis, and Corrective Action System
// Manages state for all FRACAS incidents across Magnus Resolve
// ============================================================
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    FracasIncident,
    FracasStatus,
    FracasSeverity
} from '../types/fracas.types';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { calculateFracasMetrics } from '../utils/fracas/fracasMetrics';
import {
    collection,
    query,
    where,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp
} from 'firebase/firestore';

// Context State Interface
interface FracasContextState {
    incidents: FracasIncident[];
    loading: boolean;
    error: string | null;
    selectedIncidentId: string | null;

    // Actions
    addIncident: (incident: Omit<FracasIncident, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateIncident: (id: string, updates: Partial<FracasIncident>) => Promise<void>;
    deleteIncident: (id: string) => Promise<void>;
    selectIncident: (id: string | null) => void;
    getIncidentById: (id: string) => FracasIncident | undefined;
    getIncidentsByStatus: (status: FracasStatus) => FracasIncident[];
    getIncidentsBySeverity: (severity: FracasSeverity) => FracasIncident[];
    linkToRCA: (incidentId: string, rcaId: string, rcaType: '5why' | 'ishikawa' | 'fta') => void;
    linkTo8D: (incidentId: string, eightDId: string) => void;
    refreshIncidents: () => Promise<void>;
    getMetrics: () => {
        totalIncidents: number;
        openIncidents: number;
        criticalIncidents: number;
        averageTimeToClose: number;
        incidentsByStatus: Record<string, number>;
        incidentsBySeverity: Record<string, number>;
        topFailureModes: Array<{ mode: string; count: number }>;
        monthlyTrend: Array<{ month: string; count: number }>;
    };
}

const FracasContext = createContext<FracasContextState | undefined>(undefined);

export const FracasProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [incidents, setIncidents] = useState<FracasIncident[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

    // Load incidents from Firestore
    useEffect(() => {
        // Wait for auth to fully resolve before attempting any Firestore subscription.
        // This prevents ERR_BLOCKED / permission errors during the auth loading phase.
        if (authLoading) return;
        
        if (!user) {
            setIncidents([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        
        // autoGainAdmin sees ALL incidents (no org filter); other roles scope to their org
        const isAutoGainAdmin = user.role === 'autoGainAdmin';
        const q = (isAutoGainAdmin || !user.organizationId)
            ? query(collection(db, 'fracas_incidents'))
            : query(
                collection(db, 'fracas_incidents'),
                where('organizationId', '==', user.organizationId)
              );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedIncidents: FracasIncident[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    ...data,
                    id: doc.id,
                    // Convert Firestore Timestamps to Dates
                    createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt || Date.now()),
                    updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt || Date.now()),
                    reportedAt: data.reportedAt?.toDate?.() || new Date(data.reportedAt || Date.now()),
                    complaintDate: data.complaintDate?.toDate?.() || new Date(data.complaintDate || Date.now()),
                    occurredAt: data.occurredAt?.toDate?.() || (data.occurredAt ? new Date(data.occurredAt) : undefined),
                    closedAt: data.closedAt?.toDate?.() || (data.closedAt ? new Date(data.closedAt) : undefined),
                    verifiedAt: data.verifiedAt?.toDate?.() || (data.verifiedAt ? new Date(data.verifiedAt) : undefined),
                    statusHistory: (data.statusHistory || []).map((h: any) => ({
                        ...h,
                        changedAt: h.changedAt?.toDate?.() || new Date(h.changedAt || Date.now())
                    })),
                } as FracasIncident;
            });
            
            // Sort client-side by most recently updated
            fetchedIncidents.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
            
            setIncidents(fetchedIncidents);
            setLoading(false);
        }, (err) => {
            console.error("Failed to subscribe to incidents:", err);
            setError("Failed to load incidents from database.");
            setLoading(false);
        });

        return () => unsubscribe();
    }, [authLoading, user?.uid, user?.role, user?.organizationId]);


    const addIncident = async (incidentData: Omit<FracasIncident, 'id' | 'createdAt' | 'updatedAt'>) => {
        if (!user?.organizationId) {
            throw new Error("User must be authenticated and have an organization ID.");
        }
        try {
            await addDoc(collection(db, 'fracas_incidents'), {
                ...incidentData,
                organizationId: user.organizationId,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                reportedAt: incidentData.reportedAt ? Timestamp.fromDate(incidentData.reportedAt) : Timestamp.now(),
                complaintDate: incidentData.complaintDate ? Timestamp.fromDate(incidentData.complaintDate) : Timestamp.now(),
                // Ensure other dates are converted if present
                occurredAt: incidentData.occurredAt ? Timestamp.fromDate(incidentData.occurredAt) : null,
            });
        } catch (err) {
            console.error("Error adding incident:", err);
            setError("Failed to create incident.");
            throw err;
        }
    };

    const updateIncident = async (id: string, updates: Partial<FracasIncident>) => {
        try {
            const incidentRef = doc(db, 'fracas_incidents', id);
            // Convert Dates to Timestamps for updates
            const firestoreUpdates: any = { ...updates, updatedAt: Timestamp.now() };

            if (updates.closedAt) firestoreUpdates.closedAt = Timestamp.fromDate(updates.closedAt);
            if (updates.verifiedAt) firestoreUpdates.verifiedAt = Timestamp.fromDate(updates.verifiedAt);

            await updateDoc(incidentRef, firestoreUpdates);
        } catch (err) {
            console.error("Error updating incident:", err);
            setError("Failed to update incident.");
        }
    };

    const deleteIncident = async (id: string) => {
        try {
            await deleteDoc(doc(db, 'fracas_incidents', id));
        } catch (err) {
            console.error("Error deleting incident:", err);
            setError("Failed to delete incident.");
        }
    };

    const selectIncident = (id: string | null) => {
        setSelectedIncidentId(id);
    };

    const getIncidentById = (id: string): FracasIncident | undefined => {
        return incidents.find(incident => incident.id === id);
    };

    const getIncidentsByStatus = (status: FracasStatus): FracasIncident[] => {
        return incidents.filter(incident => incident.status === status);
    };

    const getIncidentsBySeverity = (severity: FracasSeverity): FracasIncident[] => {
        return incidents.filter(incident => incident.severity === severity);
    };

    const linkToRCA = (incidentId: string) => {
        updateIncident(incidentId, {
            status: 'Analysis',
        });
    };

    const linkTo8D = (incidentId: string, eightDId: string) => {
        updateIncident(incidentId, {
            eightDId: eightDId,
            status: 'Action',
        });
    };

    const refreshIncidents = async () => {
        // No-op with Firestore listener, or force re-fetch if needed (not usually needed with onSnapshot)
        return Promise.resolve();
    };

    const getMetrics = () => calculateFracasMetrics(incidents);


    const value: FracasContextState = {
        incidents,
        loading,
        error,
        selectedIncidentId,
        addIncident,
        updateIncident,
        deleteIncident,
        selectIncident,
        getIncidentById,
        getIncidentsByStatus,
        getIncidentsBySeverity,
        linkToRCA,
        linkTo8D,
        refreshIncidents,
        getMetrics,
    };

    return <FracasContext.Provider value={value}>{children}</FracasContext.Provider>;
};

// Custom hook to use FRACAS context
export const useFracas = (): FracasContextState => {
    const context = useContext(FracasContext);
    if (!context) {
        throw new Error('useFracas must be used within a FracasProvider');
    }
    return context;
};

export default FracasContext;
